import { useMemo } from "react";
import { Search, X, ChevronDown, Filter, AlertCircle, Loader2, Image as ImageIcon, Shield, Users, Globe } from "lucide-react";
import { cn } from "../../lib/utils";
import type { 
  MapIconPickerProps, 
  MapIconPickerSetGroup,
  MapWorkspaceShareScope
} from "../../contracts/map-workspace";
import { Popover, PopoverContent, PopoverTrigger } from "../../internal/primitives/popover";
import { Input } from "../../internal/primitives/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { MapEditorEmptyState } from "./internal/MapEditorKitParts";

export function MapIconPicker(props: MapIconPickerProps) {
  const {
    presentation = "popover",
    value,
    selectedIconId,
    searchValue,
    activeSetFilter,
    setFilterOptions,
    iconSets,
    loading,
    disabled,
    readOnly,
    allowClear,
    label,
    placeholder = "Search icons...",
    helperText,
    errorMessage,
    emptyMessage = "No icons found",
    unavailableMessage,
    triggerLabel = "Select Icon",
    onSearchChange,
    onSetFilterChange,
    onSelect,
    onClear,
    className,
  } = props;

  const selectedIcon = useMemo(() => {
    for (const set of iconSets) {
      const found = set.icons.find(icon => icon.id === selectedIconId || (value && icon.token === value));
      if (found) return found;
    }
    return null;
  }, [iconSets, selectedIconId, value]);

  const content = (
    <IconPickerContent
      searchValue={searchValue}
      activeSetFilter={activeSetFilter}
      setFilterOptions={setFilterOptions}
      iconSets={iconSets}
      loading={loading}
      emptyMessage={emptyMessage}
      errorMessage={errorMessage}
      onSearchChange={onSearchChange}
      onSetFilterChange={onSetFilterChange}
      onSelect={onSelect}
      placeholder={placeholder}
      selectedIconId={selectedIconId}
      selectedSetId={props.selectedSetId}
      value={value}
    />
  );

  if (unavailableMessage) {
    return (
      <div className={cn("space-y-1.5", className)}>
        {label && <label className="text-[10px] font-black uppercase tracking-tight text-muted-foreground">{label}</label>}
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 p-3 text-[11px] text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5" />
          {unavailableMessage}
        </div>
      </div>
    );
  }

  if (presentation === "popover") {
    return (
      <div className={cn("space-y-1.5", className)}>
        {label && <label className="text-[10px] font-black uppercase tracking-tight text-muted-foreground">{label}</label>}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button
                disabled={disabled || readOnly}
                className={cn(
                  "flex h-9 flex-1 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background transition-colors focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted/50",
                  errorMessage && "border-destructive focus:ring-destructive"
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted/50 border overflow-hidden">
                    {selectedIcon?.previewUrl ? (
                      <img src={selectedIcon.previewUrl} alt="" className="h-4 w-4 object-contain" />
                    ) : (
                      <ImageIcon className="h-3 w-3 text-muted-foreground/50" />
                    )}
                  </div>
                  <span className="truncate text-[11px] font-bold">
                    {selectedIcon?.label || triggerLabel}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[320px] p-0 overflow-hidden border-border bg-background shadow-xl">
              {content}
            </PopoverContent>
          </Popover>

          {allowClear && selectedIcon && !readOnly && (
            <button
              onClick={onClear}
              disabled={disabled}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-input bg-background hover:bg-muted text-muted-foreground transition-colors"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {errorMessage ? (
          <p className="text-[10px] font-medium text-destructive">{errorMessage}</p>
        ) : helperText ? (
          <p className="text-[10px] text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  // Inline or Sheet presentation
  return (
    <div className={cn(
      "flex flex-col border rounded-lg bg-background overflow-hidden",
      presentation === "sheet" ? "h-full" : "max-h-[500px]",
      errorMessage && "border-destructive",
      className
    )}>
      {label && (
        <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
          <label className="text-[10px] font-black uppercase tracking-tight text-muted-foreground">{label}</label>
          {allowClear && selectedIcon && !readOnly && (
            <button 
              onClick={onClear} 
              className="text-[9px] font-bold text-primary hover:underline"
            >
              Clear Selection
            </button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-hidden flex flex-col">
        {content}
      </div>
      {(errorMessage || helperText) && (
        <div className="px-3 py-2 border-t bg-muted/10">
          {errorMessage ? (
            <p className="text-[10px] font-medium text-destructive">{errorMessage}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
}

interface IconPickerContentProps {
  searchValue: string;
  activeSetFilter: "all" | string;
  setFilterOptions: Array<{ value: "all" | string; label: string; disabled?: boolean }>;
  iconSets: MapIconPickerSetGroup[];
  loading?: boolean;
  emptyMessage?: string | null;
  errorMessage?: string | null;
  onSearchChange: (value: string) => void;
  onSetFilterChange: (value: "all" | string) => void;
  onSelect: (iconId: string) => void;
  placeholder: string;
  selectedIconId?: string | null;
  selectedSetId?: string | null;
  value?: string | null;
}

function IconPickerContent({
  searchValue,
  activeSetFilter,
  setFilterOptions,
  iconSets,
  loading,
  emptyMessage,
  errorMessage,
  onSearchChange,
  onSetFilterChange,
  onSelect,
  placeholder,
  selectedIconId,
  selectedSetId,
  value,
}: IconPickerContentProps) {
  const filteredSets = useMemo(() => {
    return iconSets
      .filter(set => activeSetFilter === "all" || set.id === activeSetFilter)
      .map(set => ({
        ...set,
        icons: set.icons.filter(icon => {
          if (!searchValue) return true;
          const searchLower = searchValue.toLowerCase();
          return (
            icon.label.toLowerCase().includes(searchLower) ||
            icon.token.toLowerCase().includes(searchLower) ||
            icon.keywords?.some(kw => kw.toLowerCase().includes(searchLower))
          );
        })
      }))
      .filter(set => set.icons.length > 0);
  }, [iconSets, activeSetFilter, searchValue]);

  const hasIcons = filteredSets.some(set => set.icons.length > 0);

  const getShareScopeIcon = (scope?: MapWorkspaceShareScope | null) => {
    switch (scope) {
      case "tenant": return <Shield className="h-3 w-3" />;
      case "shared": return <Users className="h-3 w-3" />;
      case "public": return <Globe className="h-3 w-3" />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-3 space-y-2 border-b bg-background/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={placeholder}
            className="pl-9 h-9 text-xs"
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Select value={activeSetFilter} onValueChange={(val) => onSetFilterChange(val as any)}>
            <SelectTrigger className="h-7 text-[10px] font-bold bg-muted/50 border-none shadow-none">
              <SelectValue placeholder="All Icon Sets" />
            </SelectTrigger>
            <SelectContent>
              {setFilterOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled} className="text-[10px] font-semibold">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-1 min-h-[200px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs font-medium">Loading icons...</span>
          </div>
        ) : errorMessage ? (
          <div className="p-4">
            <MapEditorEmptyState
              message={errorMessage}
              icon={<AlertCircle className="h-5 w-5 text-destructive" />}
            />
          </div>
        ) : !hasIcons ? (
          <div className="p-4">
            <MapEditorEmptyState
              message={emptyMessage || "No icons found"}
              icon={<Search className="h-5 w-5" />}
            />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {filteredSets.map((set) => (
              <div key={set.id} className="space-y-1.5 px-2">
                <div className={cn(
                  "flex items-center justify-between gap-2 sticky top-0 bg-background/90 backdrop-blur-sm py-1 z-[5] rounded-sm transition-colors",
                  set.id === selectedSetId && "bg-primary/5 ring-1 ring-primary/10"
                )}>
                  <div className="flex items-center gap-2">
                    <h4 className={cn(
                      "text-[10px] font-black uppercase tracking-widest px-1",
                      set.id === selectedSetId ? "text-primary" : "text-muted-foreground/70"
                    )}>
                      {set.label}
                    </h4>
                    {set.shareScope && (
                      <div className="text-muted-foreground/40" title={`Scope: ${set.shareScope}`}>
                        {getShareScopeIcon(set.shareScope)}
                      </div>
                    )}
                  </div>
                  {set.badge && (
                    <span className="text-[9px] font-bold bg-primary/10 text-primary px-1.5 rounded uppercase tracking-tighter">
                      {set.badge}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-6 gap-1 sm:grid-cols-8 md:grid-cols-6 lg:grid-cols-8">
                  {set.icons.map((icon) => {
                    const isSelected = icon.selected || icon.id === selectedIconId || (value && icon.token === value);
                    return (
                      <button
                        key={icon.id}
                        onClick={() => onSelect(icon.id)}
                        disabled={icon.disabled}
                        title={`${icon.label} (${icon.token})`}
                        className={cn(
                          "group relative aspect-square flex items-center justify-center rounded border transition-all",
                          "hover:bg-primary/5 hover:border-primary/30 hover:shadow-sm",
                          isSelected
                            ? "bg-primary/10 border-primary ring-1 ring-primary/20"
                            : "bg-card border-border",
                          icon.disabled && "opacity-40 grayscale cursor-not-allowed border-dashed"
                        )}
                      >
                        {icon.previewUrl ? (
                          <img src={icon.previewUrl} alt={icon.label} className="h-6 w-6 object-contain" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground/30" />
                        )}
                        
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

